import { useEffect, useState, useCallback } from "react";
import { Key, Plus, Trash2, Copy, Check, RefreshCw, AlertTriangle, BookOpen, Clock, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export default function IntegrationsPage() {
  const [keys, setKeys] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState({ open: false, name: "" });
  const [created, setCreated] = useState(null); // shown ONCE
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDocs, setShowDocs] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [k, j] = await Promise.all([
        api.get("/integrations/keys"),
        api.get("/integrations/sync-jobs", { params: { limit: 25 } }),
      ]);
      setKeys(k.data);
      setJobs(j.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createKey = async (e) => {
    e?.preventDefault();
    if (!newKey.name.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/integrations/keys", { name: newKey.name.trim() });
      setCreated(data);
      setNewKey({ open: false, name: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create key");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id, name) => {
    if (!confirm(`Revoke API key "${name}"? Any integration using it will stop working immediately.`)) return;
    try {
      await api.delete(`/integrations/keys/${id}`);
      toast.success(`Revoked ${name}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to revoke");
    }
  };

  const copyKey = () => {
    if (!created?.full_key) return;
    navigator.clipboard.writeText(created.full_key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="integrations-page">
      <header className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Integrations & Roster Sync</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl">
            Sync your student roster from any Student Information System (PowerSchool, Clever, Skyward, Google Classroom, etc.) using our REST API. Create one API key per integration, treat it like a password.
          </p>
        </div>
        <button onClick={load} data-testid="integrations-refresh" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition self-start">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </header>

      {/* API Keys */}
      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <div className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Key className="h-5 w-5 text-primary" /> API Keys
            </div>
            <div className="text-xs text-slate-500">{keys.filter((k) => !k.revoked_at).length} active · {keys.filter((k) => k.revoked_at).length} revoked</div>
          </div>
          <button onClick={() => setNewKey({ open: true, name: "" })} data-testid="create-api-key-btn" className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:shadow transition">
            <Plus className="h-4 w-4" /> New API key
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="api-keys-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Key prefix</th>
                <th className="px-5 py-3">Last used</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No API keys yet. Click "New API key" to create one.</td></tr>
              )}
              {keys.map((k) => (
                <tr key={k.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`api-key-${k.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{k.name}</td>
                  <td className="px-5 py-3 font-mono text-xs text-slate-700">{k.prefix}…</td>
                  <td className="px-5 py-3 text-slate-700">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : <span className="text-slate-400">Never</span>}</td>
                  <td className="px-5 py-3 text-slate-700">{new Date(k.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3">
                    {k.revoked_at ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-rose-700">Revoked</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Active</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    {!k.revoked_at && (
                      <button onClick={() => revoke(k.id, k.name)} data-testid={`revoke-key-${k.id}`} className="inline-flex items-center gap-1.5 rounded-md border border-rose-200 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50 transition">
                        <Trash2 className="h-3.5 w-3.5" /> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Recent sync jobs */}
      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <div className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Clock className="h-5 w-5 text-primary" /> Recent sync jobs
            </div>
            <div className="text-xs text-slate-500">Last 25 roster sync operations</div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="sync-jobs-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-3">When</th>
                <th className="px-5 py-3">Source</th>
                <th className="px-5 py-3">Added</th>
                <th className="px-5 py-3">Updated</th>
                <th className="px-5 py-3">Deactivated</th>
                <th className="px-5 py-3">Errors</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">No sync jobs yet.</td></tr>
              )}
              {jobs.map((j) => (
                <tr key={j.id} className="border-t border-slate-100" data-testid={`sync-job-${j.id}`}>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{new Date(j.started_at).toLocaleString()}</td>
                  <td className="px-5 py-3 text-slate-700">{j.source_key_name || "—"}</td>
                  <td className="px-5 py-3 font-semibold text-emerald-700">{j.added}</td>
                  <td className="px-5 py-3 font-semibold text-blue-700">{j.updated}</td>
                  <td className="px-5 py-3 font-semibold text-amber-700">{j.deactivated}</td>
                  <td className="px-5 py-3">
                    {j.errors.length === 0 ? <span className="text-slate-400">0</span> : (
                      <span title={j.errors.join("\n")} className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-rose-700">
                        <AlertTriangle className="h-3 w-3" /> {j.errors.length}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Docs */}
      <section className="rounded-xl border border-slate-200 bg-white">
        <button
          type="button"
          onClick={() => setShowDocs((s) => !s)}
          data-testid="toggle-docs"
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition"
        >
          <div className="flex items-center gap-2 text-slate-900">
            <BookOpen className="h-5 w-5 text-primary" />
            <span className="font-display text-lg font-semibold">API Reference</span>
            <span className="text-xs text-slate-500 font-normal">Click to {showDocs ? "hide" : "show"}</span>
          </div>
          <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${showDocs ? "rotate-180" : ""}`} />
        </button>
        {showDocs && (
          <div className="px-5 pb-6 pt-2 text-sm space-y-4 border-t border-slate-100">
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Endpoint</h3>
              <code className="block bg-slate-900 text-emerald-300 rounded-lg p-3 font-mono text-xs overflow-x-auto" data-testid="docs-endpoint">
                POST {window.location.origin}/api/integrations/students/sync
              </code>
              <p className="mt-2 text-slate-600">Authenticate via <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">X-API-Key: vlk_…</code> header (or <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">Authorization: Bearer vlk_…</code>).</p>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Request body</h3>
              <code className="block bg-slate-900 text-slate-200 rounded-lg p-3 font-mono text-xs whitespace-pre overflow-x-auto">{`{
  "students": [
    {
      "external_id": "SIS-12345",        // your unique ID (preferred for matching)
      "full_name": "Sam Smith",          // required
      "grade": "3rd",                    // optional
      "active": true,                    // false = soft-delete
      "authorized_pickups": [
        { "name": "Jane Smith", "license_number": "D1234567",
          "relationship": "mother", "phone": "555-0001" }
      ]
    }
  ],
  "full_replace": false                  // true = remove students not in payload
}`}</code>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">Matching logic</h3>
              <ol className="list-decimal pl-5 text-slate-600 space-y-1">
                <li>Match by <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">external_id</code> (if provided).</li>
                <li>Fallback: case-insensitive <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">full_name</code> match.</li>
                <li>No match → insert new student.</li>
                <li>If <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">full_replace=true</code>, any existing student NOT in the payload is set <code className="px-1.5 py-0.5 bg-slate-100 rounded text-xs">active=false</code> (soft-delete).</li>
              </ol>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-1">curl example</h3>
              <code className="block bg-slate-900 text-slate-200 rounded-lg p-3 font-mono text-xs whitespace-pre overflow-x-auto" data-testid="docs-curl">{`curl -X POST ${window.location.origin}/api/integrations/students/sync \\
  -H "X-API-Key: vlk_yourkeyhere" \\
  -H "Content-Type: application/json" \\
  -d '{"students":[{"external_id":"SIS-12345","full_name":"Sam Smith","grade":"3rd"}]}'`}</code>
            </div>
          </div>
        )}
      </section>

      {/* Create-key dialog */}
      <Dialog open={newKey.open} onOpenChange={(o) => setNewKey({ open: o, name: "" })}>
        <DialogContent className="sm:max-w-md" data-testid="create-key-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Key className="h-5 w-5 text-primary" /> New API key</DialogTitle>
            <DialogDescription>Give the key a descriptive name (e.g. "Clever production", "Manual test webhook"). The full key will only be shown once.</DialogDescription>
          </DialogHeader>
          <form onSubmit={createKey} className="space-y-3 pt-2">
            <label className="block">
              <span className="block mb-1.5 text-xs uppercase tracking-widest text-slate-500">Key name</span>
              <input autoFocus required value={newKey.name} onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
                placeholder="e.g. Clever production" data-testid="new-key-name"
                className="w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none" />
            </label>
            <DialogFooter className="pt-2 gap-2">
              <button type="button" onClick={() => setNewKey({ open: false, name: "" })} data-testid="new-key-cancel" className="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-100">Cancel</button>
              <button type="submit" disabled={busy || !newKey.name.trim()} data-testid="new-key-submit" className="h-10 rounded-lg bg-primary px-4 text-sm font-medium text-white inline-flex items-center gap-2 disabled:opacity-60">
                {busy && <RefreshCw className="h-4 w-4 animate-spin" />} Create
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Created-key dialog — show full key ONCE */}
      <Dialog open={!!created} onOpenChange={(o) => !o && setCreated(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="created-key-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Check className="h-5 w-5 text-emerald-600" /> Save this key now</DialogTitle>
            <DialogDescription>
              This is the only time we'll show the full key. Copy it and store it in your SIS / secrets manager. If lost, revoke and create a new one.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>Treat this key like a password. Anyone with this key can update your student roster.</span>
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all bg-slate-900 text-emerald-300 rounded-lg px-3 py-2.5 font-mono text-xs" data-testid="created-key-value">{created?.full_key}</code>
              <button onClick={copyKey} data-testid="copy-created-key" className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2.5 text-xs font-semibold text-white hover:shadow transition">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <DialogFooter className="pt-2">
              <button onClick={() => setCreated(null)} data-testid="created-key-done" className="h-10 rounded-lg bg-slate-900 px-4 text-sm font-medium text-white">I've saved it</button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
